import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import YourMinute from "@/components/YourMinute";
import AdviceMeter from "@/components/AdviceMeter";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowRight, Check, RefreshCw, Sparkle, CircleCheck, Clock3, Timer,
  TriangleAlert, X, DollarSign,
} from "lucide-react";

const PLACEHOLDERS = [
  "Should I lower my price?",
  "Why aren't visitors converting?",
  "Which feature should I build first?",
  "My client keeps expanding the project.",
  "How should I find my first ten customers?",
];

const OUTCOME_CHANGES = ["Decision made", "Time saved", "Money saved", "Cost avoided", "Revenue generated", "New customer won", "Conversion improved", "Task completed", "Other"];

const ANALYSIS_STAGES = [
  "Understanding your blocker…",
  "Checking your business context…",
  "Eliminating distractions…",
  "Choosing the highest-leverage move…",
  "Compiling your Minute…",
];

function Loading() {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStage((s) => Math.min(s + 1, ANALYSIS_STAGES.length - 1)), 3800);
    return () => clearInterval(t);
  }, []);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-24" data-testid="analysis-loading">
      <div className="relative h-16 w-16">
        <div className="absolute inset-0 animate-spin rounded-full border-2 border-white/10 border-t-[var(--amber)]" />
        <Sparkle className="absolute inset-0 m-auto h-6 w-6 text-[var(--amber)]" />
      </div>
      <div className="mt-8 h-6 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.p key={stage} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}
            className="font-mono2 text-xs uppercase tracking-[0.25em] text-neutral-300" data-testid="analysis-stage">
            {ANALYSIS_STAGES[stage]}
          </motion.p>
        </AnimatePresence>
      </div>
      <div className="mt-5 flex gap-1.5" aria-hidden="true">
        {ANALYSIS_STAGES.map((_, i) => (
          <span key={i} className="h-1 w-8 rounded-full transition-colors duration-500" style={{ background: i <= stage ? "var(--amber)" : "rgba(255,255,255,0.1)" }} />
        ))}
      </div>
      <p className="mt-5 text-sm text-neutral-600">One problem. One move. Usually 15–25 seconds.</p>
    </motion.div>
  );
}

function AnalysisFailed({ onRetry, onEdit }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-white/10 bg-[var(--surface)] p-8 text-center sm:p-10" data-testid="analysis-failed">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--amber)]/15"><TriangleAlert className="h-6 w-6 text-[var(--amber)]" /></span>
      <h2 className="font-head mt-5 text-2xl font-black tracking-tight text-white sm:text-3xl">Your Minute got interrupted.</h2>
      <p className="mt-2 text-sm text-neutral-400">The analysis engine didn't answer in time. Your blocker is still here — try again.</p>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
        <Button data-testid="analysis-retry-btn" onClick={onRetry} className="btn-amber px-6 py-5"><RefreshCw className="mr-1 h-4 w-4" /> Try again</Button>
        <Button data-testid="analysis-edit-btn" variant="outline" onClick={onEdit} className="border-white/15 px-6 py-5 text-white hover:bg-white/5">Edit my blocker</Button>
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("idle"); // idle|loading|clarify|result|blocked|committed|outcome
  const [text, setText] = useState("");
  const [data, setData] = useState(null);
  const [blockerId, setBlockerId] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [changedMode, setChangedMode] = useState(false);
  const [changedReason, setChangedReason] = useState("");
  const [deepFocus, setDeepFocus] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    if ((step === "result" || step === "blocked") && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  const analyze = async (payload) => {
    setStep("loading");
    const body = threadId && !payload.thread_id ? { ...payload, thread_id: threadId } : payload;
    setLastPayload(body);
    try {
      const { data: res } = await api.post("/blockers/analyze", body);
      setData(res);
      if (res.thread_id) setThreadId(res.thread_id);
      if (res.blocked) { setChangedMode(false); setStep("blocked"); if (payload.changed_reason) toast.error("That doesn't change the decision. Your recommendation stands."); }
      else if (res.needs_clarification) { setBlockerId(res.blocker_id); setStep("clarify"); }
      else { setBlockerId(res.blocker_id); setStep("result"); }
    } catch (err) {
      const detail = apiError(err.response?.data?.detail);
      if (err.response?.status && err.response.status < 500 && detail) toast.error(detail);
      setStep("failed");
    }
  };

  const submit = () => {
    if (text.trim().length < 3) { toast.error("Describe your blocker in a sentence."); return; }
    analyze({ text });
  };

  const answerClarify = (option) => analyze({ text, clarification_answer: option });

  const commit = async () => {
    try {
      await api.post(`/blockers/${blockerId}/commit`, { status: "committed" });
      setStep("committed");
    } catch (err) { toast.error("Could not save commitment"); }
  };

  const reconsider = async () => {
    try { await api.post(`/blockers/${blockerId}/commit`, { status: "reconsider" }); } catch {}
    const n = (data?.reconsideration_count || 0) + 1;
    toast(n >= 2 ? "One more alternative — then it's time to test." : "Reframe one variable, then decide.", { icon: "⟲" });
    reset(true);
  };

  const reset = (keepText = false) => {
    setStep("idle"); setData(null); setBlockerId(null); setChangedMode(false); setChangedReason("");
    if (!keepText) { setText(""); setThreadId(null); }
  };

  const submitChanged = () => {
    if (changedReason.trim().length < 12) { toast.error("Tell us specifically what changed."); return; }
    analyze({ text, changed_reason: changedReason, thread_id: threadId || data?.thread_id });
  };

  return (
    <div className="relative min-h-screen">
      <Header />
      {deepFocus && <DeepFocusOverlay action={data?.recommendation?.next_15_minutes} until={deepFocus} onExit={() => setDeepFocus(null)} onDone={() => { setDeepFocus(null); setStep("outcome"); }} />}

      <main className="relative z-10 mx-auto max-w-3xl px-5 py-10 sm:py-14">
        {step === "idle" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="eyebrow">Hi {user?.first_name || "there"} — one blocker at a time</span>
            <h1 className="font-head mt-3 text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl">
              WHAT'S BLOCKING YOU?
            </h1>
            <p className="mt-3 max-w-xl text-lg text-neutral-400">
              Bring one business blocker. Get one decisive next move. Then go execute.
            </p>

            <div className="mt-8 rounded-2xl border border-white/10 bg-[var(--surface)] p-2 focus-within:border-white/25 focus-within:amber-glow transition-all">
              <Textarea
                data-testid="blocker-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
                placeholder="Describe the one decision or problem holding you back right now…"
                className="min-h-[120px] resize-none border-0 bg-transparent text-lg text-white placeholder:text-neutral-600 focus-visible:ring-0"
              />
              <div className="flex items-center justify-between px-2 pb-1">
                <span className="font-mono2 hidden text-[10px] uppercase tracking-wider text-neutral-600 sm:block">⌘ + Enter to submit</span>
                <Button data-testid="get-move-btn" onClick={submit} className="btn-amber ml-auto rounded-full px-6 py-5 text-sm font-bold tracking-wide">
                  GET MY NEXT MOVE <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {PLACEHOLDERS.map((p) => (
                <button key={p} data-testid={`example-${p.slice(0, 8)}`} onClick={() => setText(p)}
                  className="rounded-full border border-white/10 bg-[var(--surface-raised)] px-3.5 py-1.5 text-sm text-neutral-400 transition-colors hover:border-white/25 hover:text-white">
                  {p}
                </button>
              ))}
            </div>

            {user && !user.onboarded && (
              <button data-testid="context-nudge" onClick={() => navigate("/onboarding")}
                className="mt-8 flex w-full items-center justify-between rounded-xl border border-white/10 bg-[var(--surface)] px-5 py-4 text-left transition-colors hover:border-[var(--amber)]/40">
                <div>
                  <p className="text-sm font-semibold text-white">Add business context <span className="font-normal text-neutral-500">(optional · 30s)</span></p>
                  <p className="mt-0.5 text-xs text-neutral-500">Sharper moves when MinuteSwap knows what you sell and who buys it.</p>
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-neutral-500" />
              </button>
            )}
          </motion.div>
        )}

        {step === "loading" && <Loading />}

        {step === "failed" && <AnalysisFailed onRetry={() => analyze(lastPayload || { text })} onEdit={() => reset(true)} />}

        {step === "clarify" && data && (
          <motion.div ref={resultRef} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-white/10 bg-[var(--surface)] p-7 sm:p-9">
            <span className="eyebrow">Ask Sharpener · one question only</span>
            <span className="ml-2 rounded-full border border-white/12 px-2.5 py-1 font-mono2 text-[10px] uppercase text-neutral-400">{data.category}</span>
            <h2 className="font-head mt-3 text-2xl font-extrabold text-white sm:text-3xl" data-testid="sharpener-question">{data.clarification_question}</h2>
            <p className="mt-2 text-sm text-neutral-500">Pick the closest. This is the only question — then you get your move.</p>
            <div className="mt-6 space-y-2.5">
              {data.clarification_options?.map((o, i) => (
                <button key={i} data-testid={`sharpener-option-${i}`} onClick={() => answerClarify(o)}
                  className="group flex w-full items-center justify-between rounded-xl border border-white/10 bg-[var(--surface-raised)] px-5 py-4 text-left transition-all hover:border-[var(--amber)]/50 hover:bg-[var(--amber)]/[0.04]">
                  <span className="text-[15px] text-neutral-200 group-hover:text-white">{o}</span>
                  <ArrowRight className="h-4 w-4 text-neutral-600 transition-colors group-hover:text-[var(--amber)]" />
                </button>
              ))}
            </div>
            <button onClick={() => reset(true)} className="mt-5 text-sm text-neutral-500 hover:text-neutral-300">Rephrase my blocker instead</button>
          </motion.div>
        )}

        {step === "blocked" && data && (
          <motion.div ref={resultRef} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.05] p-7 sm:p-9" data-testid="avoidance-block">
            <AdviceMeter state="decision_avoidance" reconsiderations={data.reconsideration_count} />
            <div className="mt-6 flex items-start gap-3">
              <TriangleAlert className="mt-1 h-6 w-6 shrink-0 text-[var(--crimson)]" />
              <div>
                <h2 className="font-head text-2xl font-extrabold text-white sm:text-3xl">YOU'RE NO LONGER LOOKING FOR INFORMATION.</h2>
                <p className="mt-2 text-lg text-neutral-300">You're delaying the decision.</p>
              </div>
            </div>
            {data.previous_recommendation && (
              <div className="mt-6 rounded-xl border border-white/10 bg-[var(--surface)] p-5">
                <span className="eyebrow">Your current recommendation remains</span>
                <p className="mt-2 text-[15px] font-medium text-white" data-testid="avoidance-current-rec">{data.previous_recommendation.do_this}</p>
                {data.previous_recommendation.next_15_minutes && <p className="mt-1.5 text-sm text-neutral-400">First 15 minutes: {data.previous_recommendation.next_15_minutes}</p>}
                <p className="mt-3 text-sm text-neutral-300" data-testid="avoidance-hint">{data.hint}</p>
              </div>
            )}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button data-testid="go-test-btn" onClick={() => { toast.success("Go test it in the real world."); reset(); }} className="btn-amber flex-1 py-6 text-base">
                GO TEST IT — I'M DOING IT
              </Button>
              {!changedMode ? (
                <Button data-testid="changed-btn" variant="outline" onClick={() => setChangedMode(true)} className="flex-1 border-white/15 py-6 text-white hover:bg-white/5">
                  MY CIRCUMSTANCES HAVE MATERIALLY CHANGED
                </Button>
              ) : null}
            </div>
            {changedMode && (
              <div className="mt-4 rounded-xl border border-white/10 bg-[var(--surface)] p-4">
                <p className="text-sm text-neutral-300">What's actually changed since the previous recommendation? New evidence, numbers or customer feedback reopen the analysis — another rephrasing won't.</p>
                <Input data-testid="changed-reason-input" value={changedReason} onChange={(e) => setChangedReason(e.target.value)}
                  className="mt-2 border-white/12 bg-[var(--surface-raised)] text-white" placeholder="e.g. A competitor just cut their price by 40%" />
                <Button data-testid="changed-submit" onClick={submitChanged} className="btn-amber mt-3">Re-analyse</Button>
              </div>
            )}
          </motion.div>
        )}

        {step === "result" && data && (
          <div ref={resultRef}>
            <div className="mb-5 rounded-xl border border-white/10 bg-[var(--surface-raised)] px-5 py-4">
              <AdviceMeter state={data.advice_meter} reconsiderations={data.reconsideration_count} />
            </div>
            {data.advice_meter === "loop_forming" && (
              <div className="mb-5 flex items-start gap-3 rounded-xl border border-[var(--amber)]/40 bg-[var(--amber)]/[0.07] px-5 py-4" data-testid="loop-warning">
                <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-[var(--amber)]" />
                <div>
                  <p className="text-sm font-semibold text-white">You're circling the same decision. Another answer may not improve it.</p>
                  <p className="mt-0.5 text-xs text-neutral-400">This is your final alternative for this blocker. The next ask triggers the Advice Meter's intervention — commit to one and test it.</p>
                </div>
              </div>
            )}
            {data.reopened && (
              <div className="mb-5 rounded-xl border border-[var(--emerald)]/30 bg-[var(--emerald)]/[0.06] px-5 py-3 text-sm text-neutral-300" data-testid="reopened-banner">
                Circumstances materially changed — fresh analysis recorded on the same Decision Thread.
              </div>
            )}
            <YourMinute category={data.category} recommendation={data.recommendation} />

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              className="mt-6 rounded-2xl border border-white/10 bg-[var(--surface)] p-7 text-center">
              <h3 className="font-head text-2xl font-extrabold tracking-tight text-white">WILL YOU MAKE THIS MOVE?</h3>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Button data-testid="commit-yes-btn" onClick={commit} className="btn-amber px-7 py-6 text-base">
                  <Check className="mr-1 h-5 w-5" /> YES — I'M DOING IT
                </Button>
                <Button data-testid="commit-reconsider-btn" variant="outline" onClick={reconsider} className="border-white/15 px-7 py-6 text-base text-white hover:bg-white/5">
                  <RefreshCw className="mr-1 h-4 w-4" /> I need to reconsider
                </Button>
              </div>
              <p className="mt-4 text-xs text-neutral-600">Advice without action is another form of procrastination.</p>
            </motion.div>
          </div>
        )}

        {step === "committed" && (
          <FocusGate
            action={data?.recommendation?.next_15_minutes}
            onDidIt={() => setStep("outcome")}
            onLater={() => { toast("We'll be here when you need another minute."); navigate("/moves"); }}
            onAsk={() => reset()}
            onDeepFocus={(until) => setDeepFocus(until)}
          />
        )}

        {step === "outcome" && (
          <OutcomeCheckIn blockerId={blockerId} onDone={() => { toast.success("Impact logged. Nicely done."); navigate("/impact"); }} onSkip={() => reset()} />
        )}
      </main>
    </div>
  );
}

function FocusGate({ action, onDidIt, onLater, onAsk, onDeepFocus }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-[var(--surface)] p-8 text-center sm:p-14" data-testid="focus-gate">
      <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(500px circle at 50% -10%, rgba(255,107,53,0.12), transparent 60%)" }} />
      <div className="relative z-10">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: "spring", stiffness: 200 }}
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[var(--emerald)]/15">
          <CircleCheck className="h-8 w-8 text-[var(--emerald)]" />
        </motion.div>
        <h2 className="font-head mt-6 text-3xl font-black tracking-tight text-white sm:text-4xl">YOU'VE GOT YOUR NEXT MOVE.</h2>
        <p className="mt-4 text-lg text-neutral-300">More advice won't help until you act.</p>
        <p className="mt-1 text-lg font-semibold text-[var(--amber)]">Go do it.</p>
        {action && (
          <div className="mx-auto mt-6 max-w-md rounded-xl border border-white/10 bg-[var(--surface-raised)] px-5 py-4 text-left">
            <span className="eyebrow">Your first 15 minutes</span>
            <p className="mt-1.5 text-[15px] text-neutral-200">{action}</p>
          </div>
        )}
        <p className="mt-6 text-sm text-neutral-500">You have what you came for. We'll be here when you need another minute.</p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button data-testid="did-it-btn" onClick={onDidIt} className="btn-amber px-7 py-6 text-base">I did it</Button>
          <Button data-testid="remind-later-btn" variant="outline" onClick={onLater} className="border-white/15 px-7 py-6 text-base text-white hover:bg-white/5">Remind me later</Button>
        </div>

        <div className="mt-6 flex flex-col items-center gap-3">
          <span className="eyebrow">Optional · Deep Focus</span>
          <div className="flex flex-wrap justify-center gap-2">
            {[["1 hour", "1h"], ["4 hours", "4h"], ["Until tomorrow", "tomorrow"]].map(([label, val]) => (
              <button key={val} data-testid={`deepfocus-${val}`} onClick={() => onDeepFocus(val)}
                className="flex items-center gap-1.5 rounded-full border border-white/12 px-4 py-2 text-sm text-neutral-300 hover:border-[var(--amber)]/50 hover:text-white">
                <Timer className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
          <button data-testid="ask-another-btn" onClick={onAsk} className="mt-1 text-xs text-neutral-600 hover:text-neutral-400">Ask another question</button>
        </div>
      </div>
    </motion.div>
  );
}

function DeepFocusOverlay({ action, until, onExit, onDone }) {
  const [secs, setSecs] = useState(15 * 60);
  useEffect(() => {
    const t = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#050506] px-6" data-testid="deep-focus-overlay">
      <span className="eyebrow">Deep Focus · committed until {until === "tomorrow" ? "tomorrow" : until}</span>
      <p className="font-head mt-6 max-w-2xl text-center text-2xl font-bold leading-snug text-white sm:text-3xl">{action || "Execute your committed move."}</p>
      <div className="font-mono2 my-10 text-7xl font-semibold tabular-nums text-[var(--amber)] sm:text-8xl">{mm}:{ss}</div>
      <p className="max-w-md text-center text-sm text-neutral-500">You committed to executing this move before seeking more advice. Stay with it.</p>
      <div className="mt-8 flex gap-3">
        <Button data-testid="deepfocus-complete" onClick={onDone} className="btn-amber px-6 py-5">Action complete</Button>
        <Button data-testid="deepfocus-exit" variant="outline" onClick={onExit} className="border-white/15 px-6 py-5 text-white hover:bg-white/5">I genuinely need to change course</Button>
      </div>
    </motion.div>
  );
}

function OutcomeCheckIn({ blockerId, onDone, onSkip }) {
  const [status, setStatus] = useState(null);
  const [changes, setChanges] = useState([]);
  const [minutes, setMinutes] = useState("");
  const [money, setMoney] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (c) => setChanges((x) => (x.includes(c) ? x.filter((y) => y !== c) : [...x, c]));

  const submit = async () => {
    if (!status) { toast.error("What happened?"); return; }
    setBusy(true);
    try {
      await api.post(`/blockers/${blockerId}/outcome`, {
        status, changes,
        minutes_saved: minutes ? Number(minutes) : null,
        money_value: money ? Number(money) : null,
        note: note || null,
      });
      onDone();
    } catch (err) { toast.error("Could not save outcome"); setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-white/10 bg-[var(--surface)] p-7 sm:p-9" data-testid="outcome-checkin">
      <span className="eyebrow">Outcome check-in</span>
      <h2 className="font-head mt-2 text-3xl font-black tracking-tight text-white">WHAT HAPPENED?</h2>
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {[["completed", "Completed", "#10B981"], ["partial", "Partly completed", "#F59E0B"], ["didnt", "Didn't do it", "#F43F5E"]].map(([v, label, c]) => (
          <button key={v} data-testid={`outcome-${v}`} onClick={() => setStatus(v)}
            className={`rounded-xl border px-4 py-4 text-left transition-all ${status === v ? "border-current" : "border-white/10 hover:border-white/25"}`}
            style={{ color: status === v ? c : "#A0A6B2", background: status === v ? c + "12" : "transparent" }}>
            <span className="text-sm font-semibold" style={{ color: status === v ? "#fff" : "#A0A6B2" }}>{label}</span>
          </button>
        ))}
      </div>

      {status && status !== "didnt" && (
        <div className="mt-6 space-y-5">
          <div>
            <span className="eyebrow">What changed? <span className="text-neutral-600">(optional)</span></span>
            <div className="mt-2 flex flex-wrap gap-2">
              {OUTCOME_CHANGES.map((c) => (
                <button key={c} data-testid={`change-${c.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => toggle(c)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm transition-all ${changes.includes(c) ? "border-[var(--amber)] bg-[var(--amber)]/10 text-white" : "border-white/12 text-neutral-400 hover:border-white/30"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <span className="eyebrow flex items-center gap-1.5"><Clock3 className="h-3 w-3" /> Time saved (mins)</span>
              <Input data-testid="outcome-minutes" type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} className="mt-1.5 border-white/12 bg-[var(--surface-raised)] text-white" placeholder="e.g. 60" />
            </div>
            <div>
              <span className="eyebrow flex items-center gap-1.5"><DollarSign className="h-3 w-3" /> Reported £/$ value</span>
              <Input data-testid="outcome-money" type="number" value={money} onChange={(e) => setMoney(e.target.value)} className="mt-1.5 border-white/12 bg-[var(--surface-raised)] text-white" placeholder="e.g. 500" />
            </div>
          </div>
          <p className="text-xs text-neutral-600">Figures are your own reported impact — MinuteSwap records, it doesn't claim to have generated them.</p>
        </div>
      )}

      {status && (
        <div className="mt-5">
          <span className="eyebrow">One-line note <span className="text-neutral-600">(optional)</span></span>
          <Input data-testid="outcome-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5 border-white/12 bg-[var(--surface-raised)] text-white" placeholder="What actually happened?" />
        </div>
      )}

      <div className="mt-7 flex items-center justify-between">
        <button data-testid="outcome-skip" onClick={onSkip} className="text-sm text-neutral-500 hover:text-neutral-300">Skip</button>
        <Button data-testid="outcome-submit" onClick={submit} disabled={busy} className="btn-amber rounded-full px-6">
          {busy ? "Saving…" : "Log my impact"}
        </Button>
      </div>
    </motion.div>
  );
}
